const UI_SKIN_OVERRIDE_KEY = "uiSkinOverride";
const DEFAULT_SKIN_FALLBACK = "classic";
const DEMO_SKIN_FALLBACK = "athena";

const ALLOWED_SKINS = ["classic", "athena"] as const;

export type UiSkin = (typeof ALLOWED_SKINS)[number];

import { getBrandAssets } from "@/lib/brandAssets";

const SKIN_ICON_BY_VARIANT: Record<UiSkin, { favicon: string; appleTouchIcon: string; themeColor: string }> = {
  classic: {
    favicon: getBrandAssets("classic").favicon,
    appleTouchIcon: getBrandAssets("classic").appleTouchIcon,
    themeColor: "#0d0c12",
  },
  athena: {
    favicon: "/icons/athena-favicon.png",
    appleTouchIcon: "/icons/athena-webclip.png",
    themeColor: "#000000",
  },
};

const normalizeSkin = (value: string | null | undefined): UiSkin | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return ALLOWED_SKINS.includes(normalized as UiSkin) ? (normalized as UiSkin) : null;
};

const parseAllowlist = (rawAllowlist: string | undefined): Set<string> => {
  if (!rawAllowlist) return new Set();

  return new Set(
    rawAllowlist
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean),
  );
};

const getAllowlistedUserIds = (): Set<string> =>
  parseAllowlist(import.meta.env.VITE_UI_SKIN_ALLOWLIST);

export const getDefaultSkin = (): UiSkin =>
  normalizeSkin(import.meta.env.VITE_DEFAULT_SKIN) ?? DEFAULT_SKIN_FALLBACK;

export const getDemoSkin = (): UiSkin =>
  normalizeSkin(import.meta.env.VITE_DEMO_SKIN) ?? DEMO_SKIN_FALLBACK;

export const getSkinOverride = (): UiSkin | null => {
  if (typeof window === "undefined") return null;
  return normalizeSkin(window.localStorage.getItem(UI_SKIN_OVERRIDE_KEY));
};

export const resolveUiSkin = (userId?: string | null): UiSkin => {
  const localOverride = getSkinOverride();
  if (localOverride) return localOverride;

  if (userId && getAllowlistedUserIds().has(userId)) {
    return getDemoSkin();
  }

  return getDefaultSkin();
};

export const applyUiSkin = (skin: UiSkin): void => {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.uiSkin = skin;

  const skinIcons = SKIN_ICON_BY_VARIANT[skin];
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon) {
    favicon.setAttribute("href", skinIcons.favicon);
  }

  const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleTouchIcon) {
    appleTouchIcon.setAttribute("href", skinIcons.appleTouchIcon);
  }

  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute("content", skinIcons.themeColor);
  }
};

export const initializeUiSkin = (): UiSkin => {
  const resolved = resolveUiSkin();
  applyUiSkin(resolved);
  return resolved;
};

